/*
   Fetch wrapper for the SPA.

   Auth model:
   - The access token (JWT) is held in memory by AuthContext and exposed to
     this module via `setAccessToken(...)`. Every request adds it as
     `Authorization: Bearer <token>`.
   - The refresh cookie (`rt`) is `httpOnly` and scoped to `/api/auth`. The
     browser sends it automatically on refresh / logout calls.
   - On a 401 from any API call, the wrapper transparently calls
     `POST /api/auth/refresh` once, swaps in the new access token, and
     retries the original request. A failed refresh hands control back to
     AuthContext, which routes the user to /login.
*/

export class ApiError extends Error {
	/*
     `body` carries the parsed JSON response when the server returned one,
     so callers can switch on structured fields (e.g. `body.limit` /
     `body.received` for batch_too_large) instead of parsing prose. `null`
     when the response wasn't JSON or had no body.
  */
	constructor(
		public status: number,
		message: string,
		public body: Record<string, unknown> | null = null,
	) {
		super(message);
	}
}

// In-memory access token. AuthContext wires both this getter and a setter so
// the wrapper has a stable handle on the latest value without a subscription.
let fAccessToken: string | null = null; // current Bearer token (null when signed out)
let fOnAccessChange: ((inToken: string | null) => void) | null = null; // notify AuthContext on rotation
let fOnAuthLost: (() => void) | null = null; // refresh failed → AuthContext clears user

/* Wire from AuthContext at startup. */
export function setAccessToken(inToken: string | null) {
	fAccessToken = inToken;
}
export function setAuthCallbacks(inOnChange: (inToken: string | null) => void, inOnAuthLost: () => void) {
	fOnAccessChange = inOnChange;
	fOnAuthLost = inOnAuthLost;
}

/* In-flight refresh: only one /refresh call at a time. Concurrent 401s from
   parallel requests should all wait on the same promise to avoid blowing
   through the rotation budget and triggering a reuse alarm. */
let fRefreshInflight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
	if (!fRefreshInflight) {
		fRefreshInflight = (async () => {
			try {
				const res = await fetch('/api/auth/refresh', {
					method: 'POST',
					credentials: 'include',
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				});
				if (!res.ok) return null;
				const body = (await res.json()) as { accessToken: string };
				fAccessToken = body.accessToken;
				if (fOnAccessChange) fOnAccessChange(body.accessToken);
				return body.accessToken;
			} catch {
				return null;
			} finally {
				fRefreshInflight = null;
			}
		})();
	}
	return fRefreshInflight;
}

function buildAuthHeaders(extra?: HeadersInit): HeadersInit {
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		'X-Requested-With': 'XMLHttpRequest',
	};
	if (fAccessToken) headers['Authorization'] = `Bearer ${fAccessToken}`;
	return { ...headers, ...((extra as Record<string, string>) ?? {}) };
}

async function request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
	const res = await fetch(`/api${path}`, {
		...options,
		headers: buildAuthHeaders(options.headers),
		credentials: 'include',
	});

	// Single retry on 401: refresh the access token, swap it in, redo the call.
	// The /api/auth/* endpoints are excluded from this dance — refresh / verify
	// / logout responses are themselves the source of truth for the access half.
	if (res.status === 401 && !isRetry && !path.startsWith('/auth/')) {
		const fresh = await refreshAccessToken();
		if (fresh) return request<T>(path, options, true);
		if (fOnAuthLost) fOnAuthLost();
		throw new ApiError(401, 'Unauthenticated');
	}

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new ApiError(res.status, body.error ?? res.statusText, body);
	}
	if (res.status === 204) return undefined as T;
	return res.json();
}

/* Multipart upload — same auth path, but no Content-Type so the browser builds
   the multipart boundary itself. */
async function upload<T>(path: string, formData: FormData, isRetry = false): Promise<T> {
	const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
	if (fAccessToken) headers['Authorization'] = `Bearer ${fAccessToken}`;

	const res = await fetch(`/api${path}`, {
		method: 'POST',
		body: formData,
		credentials: 'include',
		headers,
	});

	if (res.status === 401 && !isRetry && !path.startsWith('/auth/')) {
		const fresh = await refreshAccessToken();
		if (fresh) return upload<T>(path, formData, true);
		if (fOnAuthLost) fOnAuthLost();
		throw new ApiError(401, 'Unauthenticated');
	}

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new ApiError(res.status, body.error ?? res.statusText, body);
	}
	if (res.status === 204) return undefined as T;
	return res.json();
}

export const api = {
	get: <T>(path: string) => request<T>(path),
	post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
	put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
	patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
	delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
	upload,

	/*
     downloadCsv used to navigate the window to the export URL; that worked
     while we relied on session cookies. With Bearer auth, the navigation
     request carries no Authorization header, so the server returns 401.
     We now fetch the CSV via XHR (so the Bearer header is added), build a
     blob URL, and trigger an anchor click — the user's experience is
     identical (the file is downloaded with the right name) and authentication
     keeps working.
  */
	async downloadCsv(path: string, params: Record<string, string | number | undefined>): Promise<void> {
		const qs = new URLSearchParams(
			Object.entries(params)
				.filter(([, v]) => v !== undefined)
				.map(([k, v]) => [k, String(v)]),
		).toString();
		const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
		if (fAccessToken) headers['Authorization'] = `Bearer ${fAccessToken}`;
		const res = await fetch(`/api${path}?${qs}`, { credentials: 'include', headers });
		if (res.status === 401) {
			const fresh = await refreshAccessToken();
			if (!fresh) {
				if (fOnAuthLost) fOnAuthLost();
				throw new ApiError(401, 'Unauthenticated');
			}
			return this.downloadCsv(path, params);
		}
		if (!res.ok) throw new ApiError(res.status, res.statusText);
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const filename = res.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/i)?.[1] ?? 'export.csv';
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	},
};
